import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackFreeSignupClick, trackDemoBookedClick } from "../utils/conversions";
import { useTranslation } from "react-i18next";
import whitelogo from "../assets/whiteintel-white-logo-new.png";
import blacklogo from "../assets/whiteintel-black-logo-new.png";
import {
  PiUserCircleThin,
  PiCaretDownBold,
  PiXBold,
  PiListBold
} from "react-icons/pi";
import {
  LayoutDashboard,
  PlayCircle,
  ShieldAlert,
  Building2,
  Network,
  Lock,
  Briefcase,
  Bug,
  BookOpen,
  Code2,
  Globe,
  HelpCircle,
  User,
  Eye,
  Newspaper,
  ArrowRight,
} from "lucide-react";
import AnnouncementBar from "./AnnouncementBar";
import LanguageSwitcher from "./LanguageSwitcher";


// --- CONFIGURATION ---

function useMenus() {
  const { t } = useTranslation("common");
  return {
    platform: {
      width: "w-[1000px]",
      gridCols: "grid-cols-4",
      columns: [
        {
          heading: t("megamenu.platformHeading"),
          colSpan: "col-span-1",
          items: [
            { icon: LayoutDashboard, label: t("megamenu.platformOverview"), desc: t("megamenu.platformOverviewDesc"), href: "/platform-overview" },
            { icon: PlayCircle, label: t("megamenu.platformTour"), desc: t("megamenu.platformTourDesc"), href: "/platform-tour" },
          ],
        },
        {
          heading: t("megamenu.useCasesHeading"),
          colSpan: "col-span-2",
          items: [
            { icon: ShieldAlert, label: t("megamenu.atoPrevention"), desc: t("megamenu.atoPreventionDesc"), href: "/ato-prevention" },
            { icon: Network, label: t("megamenu.adProtection"), desc: t("megamenu.adProtectionDesc"), href: "/active-directory-protection" },
            { icon: Building2, label: t("megamenu.enterpriseProtection"), desc: t("megamenu.enterpriseProtectionDesc"), href: "/enterprise-protection" },
            { icon: Lock, label: t("megamenu.ransomwarePrevention"), desc: t("megamenu.ransomwarePreventionDesc"), href: "/ransomware-prevention" },
            { icon: User, label: t("megamenu.consumerProtection"), desc: t("megamenu.consumerProtectionDesc"), href: "/consumer-protection" },
            { icon: Briefcase, label: t("megamenu.msspEnrichments"), desc: t("megamenu.msspEnrichmentsDesc"), href: "/mssp-enrichments" },
            { icon: Eye, label: t("megamenu.darkwebMentionMonitoring"), desc: t("megamenu.darkwebMentionMonitoringDesc"), href: "/darkweb-mention-monitoring" },
            { icon: Globe, label: t("megamenu.lookalikeMonitoring"), desc: t("megamenu.lookalikeMonitoringDesc"), href: "/look-alike-domain-monitoring" },
            { icon: Bug, label: t("megamenu.vulnResearch"), desc: t("megamenu.vulnResearchDesc"), href: "/vulnerability-research" },
          ],
        },
      ],
      cta: {
        title: t("megamenu.ctaTitle"),
        body: t("megamenu.ctaBody"),
        action: { label: t("megamenu.ctaAction"), href: "/signup" },
      },
    },
    resources: {
      width: "w-[750px]",
      gridCols: "grid-cols-3",
      columns: [
        {
          heading: t("megamenu.knowledgeBaseHeading"),
          colSpan: "col-span-1",
          items: [
            { icon: BookOpen, label: t("megamenu.knowledgeCenter"), desc: t("megamenu.knowledgeCenterDesc"), href: "https://knowledge.whiteintel.io" },
            { icon: HelpCircle, label: t("megamenu.faq"), desc: t("megamenu.faqDesc"), href: "https://knowledge.whiteintel.io/frequently-asked-questions/faq" },
          ],
        },
        {
          heading: t("megamenu.devInsightsHeading"),
          colSpan: "col-span-2",
          items: [
            { icon: Code2, label: t("megamenu.apiDocs"), desc: t("megamenu.apiDocsDesc"), href: "https://docs.whiteintel.io" },
            { icon: Newspaper, label: t("megamenu.blog"), desc: t("megamenu.blogDesc"), href: "https://whiteintel.io/blog" },
            { icon: BookOpen, label: t("megamenu.glossary"), desc: t("megamenu.glossaryDesc"), href: "/glossary" },
          ],
        },
      ],
    },
  };
}

const THEMES = {
  light: {
    navBg: "bg-white/90 backdrop-blur-xl border-gray-200 shadow-sm",
    textMain: "text-slate-900",
    textMuted: "text-slate-600",
    textHover: "hover:text-blue-600",
    mobileToggle: "text-slate-900 hover:bg-slate-100",
    panelBg: "bg-white/95 backdrop-blur-xl border-gray-200 shadow-2xl",
    itemHover: "hover:bg-slate-50",
    headingText: "text-blue-600",
    ctaBg: "bg-gray-50 border-gray-100",
    mobileBg: "bg-white",
    mobileTextMain: "text-slate-900",
    mobileTextSub: "text-slate-500",
    mobileBorder: "border-gray-200",
    mobileIcon: "text-slate-400",
    loginBtn: "text-slate-600 hover:text-blue-600 hover:bg-slate-100",
    ctaPrimary: "bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/20",
    ctaSecondary: "bg-[#ff5120] text-white hover:bg-[#e2471d] shadow-md shadow-orange-500/20",
  },
  dark: {
    navBg: "bg-[#020617] border-white/5 shadow-lg",
    textMain: "text-white",
    textMuted: "text-slate-300",
    textHover: "hover:text-white",
    mobileToggle: "text-white hover:bg-white/10",
    panelBg: "bg-[#020617]/95 backdrop-blur-2xl border-white/10 shadow-2xl shadow-black/80", 
    itemHover: "hover:bg-white/5",
    headingText: "text-blue-400",
    ctaBg: "bg-[#0b1229] border-white/5",
    mobileBg: "bg-[#020617]",
    mobileTextMain: "text-white",
    mobileTextSub: "text-slate-400",
    mobileBorder: "border-white/10",
    mobileIcon: "text-slate-400",
    loginBtn: "text-slate-300 hover:text-white hover:bg-white/10",
    ctaPrimary: "bg-blue-600 text-white hover:bg-blue-500 shadow-[0_0_20px_-5px_rgba(37,99,235,0.5)]",
    ctaSecondary: "px-5 py-2.5 rounded-full text-sm font-bold transition-all bg-gradient-to-r from-[#ff5120] to-[#d9380b] hover:from-[#ff6b42] hover:to-[#e63b0a] text-white shadow-[0_0_20px_-5px_rgba(255,81,32,0.5)]",
  },
};

// --- MAIN COMPONENT ---

export default function Navbar({ variant = "dark" }) {
  const [openMobile, setOpenMobile] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeVariant, setActiveVariant] = useState(variant);
  const [barDismissed, setBarDismissed] = useState(
    () => sessionStorage.getItem("announcementDismissed") === "true"
  );

  const { t: tr } = useTranslation("common");
  const MENUS = useMenus();
  const t = THEMES[activeVariant] ?? THEMES.dark;

  useEffect(() => {
    const onScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  useEffect(() => {
    const close = (e) => {
      if (!document.getElementById("nav-root")?.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    if (openMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [openMobile]);

  return (
    <>

      <header
        id="nav-root"
        // ✅ FIX: top-[34px] pushes the navbar below the 34px-tall AnnouncementBar
        className={`fixed ${barDismissed ? "top-0" : "top-[0px]"} left-0 right-0 z-50 transition-all duration-300 border-b ${
          scrolled ? t.navBg : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[72px] lg:h-[80px]">
            
            {/* 1. LOGO */}
            <Link to="/" className="flex items-center gap-2 shrink-0 z-50 relative">
              <img
                src={activeVariant === "light" ? blacklogo : whitelogo}
                alt="Whiteintel"
                className="h-9 w-auto lg:h-10"
                width={160}
                height={40}
              />
              <span className={`text-xl lg:text-2xl font-extrabold tracking-tight ${t.textMain}`}>
                Whiteintel
              </span>
            </Link>

            {/* 2. DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-1 h-full">
              <MegaTrigger label={tr("nav.platform")} menuKey="platform" openMenu={openMenu} setOpenMenu={setOpenMenu} t={t} MENUS={MENUS} />
              <MegaTrigger label={tr("nav.resources")} menuKey="resources" openMenu={openMenu} setOpenMenu={setOpenMenu} t={t} MENUS={MENUS} />
              <NavLink label={tr("nav.pricing")} href="/pricing" t={t} />
              <NavLink label={tr("nav.contact")} href="/contact" t={t} />
            </nav>

            {/* 3. RIGHT ACTIONS */}
            <div className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher variant={activeVariant} />
              <button
                onClick={() => window.open("https://platform.whiteintel.io/", "_blank")}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-md font-semibold transition-colors ${t.loginBtn}`}
              >
                <PiUserCircleThin className="w-5 h-5" />
                {tr("nav.login")}
              </button>
            <button
              onClick={() => {
                trackFreeSignupClick();
                window.open("https://platform.whiteintel.io/signup", "_blank");
              }}
              className="px-5 py-2.5 rounded-full text-sm font-bold transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg shadow-blue-500/25 border border-white/10"
            >
              {tr("nav.getStarted")}
            </button>
<button
  onClick={() => {
    trackDemoBookedClick();
    window.open("https://calendly.com/whiteintel-sales", "_blank");
  }}
  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${t.ctaSecondary}`}
>
  {tr("nav.scheduleCall")}
</button>
            </div>

            {/* 4. MOBILE TOGGLE */}
            <button
              onClick={() => setOpenMobile(!openMobile)}
              className={`lg:hidden p-2 rounded-lg z-50 relative transition-colors ${t.mobileToggle}`}
            >
              {openMobile ? <PiXBold className="w-6 h-6" /> : <PiListBold className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE OVERLAY */}
      {/* ✅ FIX: pt-[114px] = 34px (announcement bar) + 80px (navbar height) */}
      <div 
        className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ease-in-out ${barDismissed ? "pt-[80px]" : "pt-[114px]"} px-6 overflow-y-auto ${t.mobileBg} ${
          openMobile ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col gap-6 pb-20">
          <MobileMenuSection title={tr("nav.platform")} menu={MENUS.platform} setOpenMobile={setOpenMobile} t={t} />
          <div className={`border-t ${t.mobileBorder}`} />
          <MobileMenuSection title={tr("nav.resources")} menu={MENUS.resources} setOpenMobile={setOpenMobile} t={t} />
          <div className={`border-t ${t.mobileBorder}`} />

          <div className={`flex flex-col gap-4 text-lg font-medium ${t.mobileTextMain}`}>
            <Link to="/pricing" onClick={() => setOpenMobile(false)}>{tr("nav.pricing")}</Link>
            <Link to="/contact" onClick={() => setOpenMobile(false)}>{tr("nav.contact")}</Link>
          </div>

          <div className="mt-4">
            <LanguageSwitcher variant={activeVariant} />
          </div>

          <div className="mt-4 flex flex-col gap-3">
        <button
          onClick={() => {
            trackFreeSignupClick();
            window.open("https://platform.whiteintel.io/signup", "_blank");
          }}
          className="px-5 py-2.5 rounded-full text-sm font-bold transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg shadow-blue-500/25 border border-white/10"
        >
          {tr("nav.getFreeAccount")}
        </button>
            <button
              onClick={() => window.open("https://platform.whiteintel.io/", "_blank")}
              className={`w-full py-3 rounded-lg border font-semibold text-center ${
                activeVariant === 'light'
                ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'border-white text-white hover:bg-white/5'
              }`}
            >
              {tr("nav.logIn")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// --- SUB COMPONENTS ---

function NavLink({ label, href, t }) {
  return (
    <Link to={href} className={`px-4 py-2 text-lg font-medium transition-colors ${t.textMuted} ${t.textHover}`}>
      {label}
    </Link>
  );
}

function MegaTrigger({ label, menuKey, openMenu, setOpenMenu, t, MENUS }) {
  const isOpen = openMenu === menuKey;

  return (
    <div className="flex items-center h-full" onMouseEnter={() => setOpenMenu(menuKey)} onMouseLeave={() => setOpenMenu(null)}>
      <button
        className={`flex items-center gap-1.5 px-4 py-2 text-lg font-medium transition-colors outline-none ${
          isOpen ? t.textMain : t.textMuted
        } ${t.textHover}`}
        onClick={() => setOpenMenu(isOpen ? null : menuKey)}
      >
        {label}
        <PiCaretDownBold className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200 origin-top ${
          isOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 translate-y-2 invisible"
        }`}
      >
        <div className={`rounded-2xl border p-6 lg:p-8 overflow-hidden ${t.panelBg} ${MENUS[menuKey].width}`}>
          <div className={`grid gap-12 ${MENUS[menuKey].gridCols}`}>
            {MENUS[menuKey].columns.map((col, idx) => (
              <div key={idx} className={col.colSpan}>
                <h4 className={`bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent text-small font-bold uppercase tracking-widest mb-4 ${t.headingText}`}>
                  {col.heading}
                </h4>
                <div className={col.colSpan === "col-span-2" ? "grid grid-cols-2 gap-x-8 gap-y-3" : "space-y-3"}>
                  {col.items.map((item) => {
                    const isExternal = item.href.startsWith("http");
                    const Tag = isExternal ? "a" : Link;
                    const linkProps = isExternal
                      ? { href: item.href }
                      : { to: item.href };
                    return (
                      <Tag
                        key={item.label}
                        {...linkProps}
                        className={`group flex items-start gap-3 p-2 -mx-2 rounded-lg transition-colors ${t.itemHover}`}
                      >
                        <div className={`mt-0.5 shrink-0 ${t.textMuted} group-hover:text-blue-500 transition-colors`}>
                          <item.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${t.textMain} group-hover:text-blue-500 transition-colors`}>
                            {item.label}
                          </div>
                          <div className={`text-[13px] leading-snug mt-0.5 ${t.textMuted}`}>
                            {item.desc}
                          </div>
                        </div>
                      </Tag>
                    );
                  })}
                </div>
              </div>
            ))}

            {MENUS[menuKey].cta && (
              <div className="col-span-1 flex flex-col">
                <div className={`flex-1 flex flex-col justify-between rounded-xl p-6 border ${t.ctaBg}`}>
                  <div>
                    <h4 className={`bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent text-m font-bold uppercase tracking-widest mb-2 ${t.textMain}`}>
                      {MENUS[menuKey].cta.title}
                    </h4>
                    <p className={`text-sm mb-4 ${t.textMuted} leading-relaxed`}>
                      {MENUS[menuKey].cta.body}
                    </p>
                  </div>
                  <Link
                    to={MENUS[menuKey].cta.action.href}
                    className="flex items-center justify-between text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors group"
                  >
                    {MENUS[menuKey].cta.action.label}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileMenuSection({ title, menu, setOpenMobile, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button 
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center justify-between w-full text-lg font-bold mb-4 ${t.textMain}`}
      >
        {title}
        <PiCaretDownBold className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="flex flex-col gap-6 pl-2 animate-in slide-in-from-top-2 duration-200">
          {menu.columns.map((col, i) => (
            <div key={i}>
              <h5 className="bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent text-m font-bold uppercase tracking-widest">{col.heading}</h5>
              <div className="space-y-4">
                {col.items.map((item) => {
                  const isExternal = item.href.startsWith("http");
                  const Tag = isExternal ? "a" : Link;
                  const linkProps = isExternal
                    ? { href: item.href }
                    : { to: item.href, onClick: () => setOpenMobile(false) };
                  return (
                    <Tag
                      key={item.label}
                      {...linkProps}
                      className="flex items-center gap-3"
                    >
                      <item.icon className={`w-5 h-5 ${t.mobileIcon}`} />
                      <span className={`text-sm font-medium ${t.mobileTextMain}`}>{item.label}</span>
                    </Tag>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}